import React, { useEffect, useMemo, useState } from 'react';
import {
    Typography,
    Grid,
    Card,
    CardContent,
    Box,
    Divider,
    IconButton,
    Button,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, useFieldArray } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import WSDateField from '../../Components/DateField/WSDateField';
import WSSelectField from '../../Components/Select/WSSelect';
import WSTextField from '../../Components/TextBox/WSTextBox';
import { HeaderList, IHeaderListActions } from '../../Components/Header/HeaderList';
import WSButton from '../../Components/Button/WSButton';
import SaveIcon from '@mui/icons-material/Save';
import { callAPI, getLocalValue, LoacalVariables, Method } from '../../commonFunctions/CommonFunctions';
import { useSnackbar } from '../../Components/Snackbar/SnackBarContext';
import { useNavigate, useParams } from 'react-router-dom';
import { WSIcons } from '../../commonFunctions/iIcons';

type VisitData = {
    date: string;
    status: string;
    notes: string;
    user: string;
    id: string
};

type YearEntry = {
    year: number;
    visits: VisitData[];
};

type FormValues = {
    entries: YearEntry[];
};

const Visit: React.FC = () => {
    const { showMessage } = useSnackbar();
    const navigate = useNavigate();
    const params = useParams();
    const id: any = params?.id;
    const visitedDate=params?.date
    const userSource: any = {
        url: 'auth/search',
        "page": 1,
        "pageSize": 10,
        "filter": [

        ],
        "sort": [

        ]
    }
    const {
        control,
        setValue,
        getValues,
        trigger,
    } = useForm<FormValues>({
        defaultValues: { entries: [] },
    });

    const { fields: yearFields, replace } = useFieldArray({
        control,
        name: 'entries',
    });

    const startDate = visitedDate;

    const yearEntries = useMemo(() => {
        const start = dayjs(startDate);
        const entries: YearEntry[] = [];

        for (let i = 0; i < 5; i++) {
            const year = start.year() + i;

            entries.push({
                year,
                visits: [0, 1, 2, 3].map((quarter) => ({

                    date: dayjs(`${year}-01-12`).add(quarter * 3, 'month').format('DD-MM-YYYY'),
                    status: 'Pending',
                    notes: '',
                    user: '',
                    id: '',
                })),
            });
        }

        return entries;
    }, [startDate]);
    const handleCancelClick = () => {
        navigate('/contacts/list')
    }
    const actions: IHeaderListActions[] =
        [
            { type: 'button', text: 'Cancel', size: 'small', onClick: handleCancelClick, variant: 'outlined', color: 'primary' },

        ]
    useEffect(() => {
        async function getVisitedItems() {
            try {
                const response: any = await callAPI('visit/lead', Method.GET, null, id);

                if (response?.data) {
                    const apiVisits = response.data;

                    // Group API visits by year
                    const groupedByYear = apiVisits.reduce((acc: Record<number, VisitData[]>, visit: any) => {
                        const year = dayjs(visit.visitingDate, 'DD-MM-YYYY').year();
                        if (!acc[year]) acc[year] = [];
                        acc[year].push({
                            date: visit.visitingDate,
                            status: visit.status,
                            notes: visit.notes,
                            user: visit.userId || '',
                            id: visit?.id
                        });
                        return acc;
                    }, {});

                    // Build merged entries
                    const mergedEntries = yearEntries.map((entry) => {
                        const year = entry.year;
                        const apiYearVisits = groupedByYear[year] || [];

                        // If fewer than 4 from API, pad with defaults
                        const remainingSlots = 4 - apiYearVisits.length;

                        const defaultDates = [0, 1, 2, 3].map((q) =>
                            dayjs(`${year}-01-12`).add(q * 3, 'month').format('DD-MM-YYYY')
                        );

                        const defaultVisits: VisitData[] = [];

                        for (let i = 0; i < 4 && defaultVisits.length < remainingSlots; i++) {
                            const defaultDate = defaultDates[i];
                            const isAlreadyUsed = apiYearVisits.some((v: any) => v.date === defaultDate);
                            if (!isAlreadyUsed) {
                                defaultVisits.push({
                                    date: defaultDate,
                                    status: 'Pending',
                                    notes: '',
                                    user: '',
                                    id: '',
                                });
                            }
                        }

                        return {
                            year,
                            visits: [...apiYearVisits, ...defaultVisits].slice(0, 4), // strictly 4 only
                        };
                    });

                    replace(mergedEntries); // Apply to form
                }
            } catch (error) {
                console.error('Error fetching visits', error);
            }
        }

        getVisitedItems();
    }, [id, replace, yearEntries]);

    useEffect(() => {
        replace(yearEntries);
    }, [yearEntries, replace]);

    const [_, forceUpdate] = useState(0);

    const handleDeleteVisit = (yearIndex: number, visitIndex: number) => {

        const allEntries = getValues('entries');
        const updatedVisits = [...allEntries[yearIndex].visits];

        if (updatedVisits.length > 1) {
            updatedVisits.splice(visitIndex, 1);
            setValue(`entries.${yearIndex}.visits`, updatedVisits);
            forceUpdate((v: number) => v + 1); // trigger re-render
        }
    }

    const handleAddVisit = (yearIndex: number) => {
        const allEntries = getValues('entries');
        const year = allEntries[yearIndex].year;
        const updatedVisits = [
            ...allEntries[yearIndex].visits,
            {
                date: dayjs(`${year}-04-12`).format('DD-MM-YYYY'),
                status: 'Pending',
                notes: '',
                user: '',
                id: ''
            },
        ];
        setValue(`entries.${yearIndex}.visits`, updatedVisits);
        forceUpdate((v) => v + 1); // <- re-render after addition
    };
    // const handleDeleteVisit = (yearIndex: number, visitIndex: number) => {
    //     debugger
    //     const allEntries = getValues('entries');
    //     const updatedVisits = [...allEntries[yearIndex].visits];
    //     updatedVisits.splice(visitIndex, 1);
    //     setValue(`entries.${yearIndex}.visits`, updatedVisits);
    // };

    // const handleAddVisit = (yearIndex: number) => {
    //     const allEntries = getValues('entries');
    //     const year = allEntries[yearIndex].year;
    //     const updatedVisits = [
    //         ...allEntries[yearIndex].visits,
    //         {
    //             date: dayjs(`${year}-04-12`).format('DD-MM-YYYY'),
    //             status: 'Pending',
    //             notes: '',
    //             user: '',
    //         },
    //     ];
    //     setValue(`entries.${yearIndex}.visits`, updatedVisits);
    // };

    const handleAdd = async (yearIndex: number, visitIndex: number) => {
        const base: any = `entries.${yearIndex}.visits.${visitIndex}`;
        

        const valid = await trigger([
            `entries.${yearIndex}.visits.${visitIndex}.date`,
            `entries.${yearIndex}.visits.${visitIndex}.status`,
            `entries.${yearIndex}.visits.${visitIndex}.notes`,
            `entries.${yearIndex}.visits.${visitIndex}.user`,
        ]);

        if (valid) {
            const visitData = getValues(base);
            const request: any = {
                "userId": visitData?.user,
                "visitingDate": visitData?.date,
                "status": visitData?.status,
                "notes": visitData?.notes,
                "leadId": id
            }
            try {
                const response: any = await callAPI('visit', Method.POST, request)
                if (response) {
                    showMessage(response?.message, response?.success ? "success" : "error")
                }
            } catch (error) {

            }
        }
    };
    const handleUpdate = async (yearIndex: number, visitIndex: number) => {
        const base: any = `entries.${yearIndex}.visits.${visitIndex}`;
        debugger

        const valid = await trigger([
            `entries.${yearIndex}.visits.${visitIndex}.date`,
            `entries.${yearIndex}.visits.${visitIndex}.status`,
            `entries.${yearIndex}.visits.${visitIndex}.notes`,
            `entries.${yearIndex}.visits.${visitIndex}.user`,
        ]);

        if (valid) {
            const visitData = getValues(base);
            const request: any = {
                "userId": visitData?.user,
                "visitingDate": visitData?.date,
                "status": visitData?.status,
                "notes": visitData?.notes,
                "leadId": id,

            }
            try {
                if (visitData?.id !== "") {
                    const response: any = await callAPI('visit', Method.PUT, request, visitData?.id)
                    if (response) {
                        showMessage(response?.message, response?.success ? "success" : "error")
                    }
                }
                else {

                    const response: any = await callAPI('visit', Method.POST, request)
                    if (response) {
                        showMessage(response?.message, response?.success ? "success" : "error")
                    }
                }

            } catch (error) {

            }
        }
    };
    const transFormData = (data: any) => {
        const options: any = []
        data?.map((item: any) => {
            const name = item?.firstName + (item?.lastName ? ' ' + item.lastName : '');
            options.push({ label: name, value: item?.id })
        })
        return options
    }
    console.log('yearFields', yearEntries)
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 2 }}>
                <HeaderList title='Yearly Visit Schedule' type='card' headerSx={{ height: 50 }} actions={actions} />

                {yearFields.map((field, yearIndex) => {
                    const yearVisits = getValues(`entries.${yearIndex}.visits`) || [];

                    return (
                        <Card key={field.id} variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
                            <CardContent>
                                <Typography sx={{ fontSize: '0.875rem' }} gutterBottom>
                                    {field.year}
                                </Typography>
                                <Divider />

                                {yearVisits.map((visit: VisitData, visitIndex: number) => (

                                    <form
                                        key={visitIndex}
                                        onSubmit={(e) => {
                                            e.preventDefault();

                                            const userValue = getValues(`entries.${yearIndex}.visits.${visitIndex}.user`);

                                            if (userValue) {
                                                // 🔁 Update mode
                                                console.log('Updating existing visit');
                                                handleUpdate(yearIndex, visitIndex);
                                            } else {
                                                // ➕ Add mode
                                                console.log('Adding new visit');
                                                handleAdd(yearIndex, visitIndex);
                                            }
                                        }}
                                    >
                                        <Grid container spacing={2} alignItems="center" sx={{ mb: 1, mt: 1 }}>
                                            <Grid item xs={12} sm={2}>
                                                <WSDateField
                                                    label={`Visit ${visitIndex + 1}`}
                                                    control={control}
                                                    name={`entries.${yearIndex}.visits.${visitIndex}.date`}
                                                    id={`entries.${yearIndex}.visits.${visitIndex}.date`}
                                                    dateFormat="DD-MM-YYYY"
                                                    rules={{ required: true }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={2}>
                                                <WSSelectField
                                                    label="User"
                                                    control={control}
                                                    name={`entries.${yearIndex}.visits.${visitIndex}.user`}
                                                    id={`entries.${yearIndex}.visits.${visitIndex}.user`}
                                                    rules={{ required: true }}
                                                    source={userSource}
                                                    transFormData={transFormData}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={2}>
                                                <WSSelectField
                                                    label="Status"
                                                    control={control}
                                                    name={`entries.${yearIndex}.visits.${visitIndex}.status`}
                                                    id={`entries.${yearIndex}.visits.${visitIndex}.status`}
                                                    rules={{ required: true }}
                                                    options={[
                                                        { label: 'Pending', value: 'Pending' },
                                                        { label: 'Completed', value: 'Completed' },
                                                    ]}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={4}>
                                                <WSTextField
                                                    label="Notes"
                                                    control={control}
                                                    name={`entries.${yearIndex}.visits.${visitIndex}.notes`}
                                                    id={`entries.${yearIndex}.visits.${visitIndex}.notes`}
                                                    rules={{ required: true }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={2} display="flex" gap={1}>

                                                <IconButton
                                                    type="submit"
                                                    color="primary"
                                                    sx={{
                                                        // border: '1px solid #fff',
                                                        // backgroundColor: '#fff',
                                                        color: '#7D79C2',
                                                        // borderRadius: 2,
                                                        // '&:hover': {
                                                        //     backgroundColor: '#5f5bb3',
                                                        // },
                                                    }}
                                                > <Tooltip title="Save">
                                                        {getValues(`entries.${yearIndex}.visits.${visitIndex}.user`) === ''
                                                            ? <SaveIcon fontSize="small" />
                                                            : <WSIcons.Edit fontSize="small" />}
                                                    </Tooltip>
                                                </IconButton>

                                                {yearVisits.length > 1 && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDeleteVisit(yearIndex, visitIndex)}
                                                    // sx={{ border: '1px solid #ccc', borderRadius: 2 }}
                                                    >
                                                        <Tooltip title="Delete">
                                                            <DeleteIcon />
                                                        </Tooltip>
                                                    </IconButton>
                                                )}

                                            </Grid>
                                        </Grid>
                                    </form>
                                ))}

                                {yearVisits.length < 4 && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleAddVisit(yearIndex)}
                                        sx={{ mt: 1 }}
                                    >
                                        Add Visit
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </LocalizationProvider>
    );
};

export default Visit;
